import { useDispatch, useSelector } from "react-redux";
import CustomCommandBar from "../../Components/CustomCommandBar/CustomCommandBar";
import Loadable from "../../Components/Loadable/Loadable";
import NavigationMenu from "../../Components/NavigationMenu/NavigationMenu";
import { ISentinelRich } from "../../Models/ISentinel";
import "./LandingPage.scss";
import { useEffect, useState } from "react";
import { GlobalState } from "../../Redux/RootReducer";
import ApiService from "../../Services/ApiService";
import { GenericActions } from "../../Redux/Generic/GenericAction";
import { ToastMessage } from "../../Utils/UIMessages";
import { IAPIResponse } from "../../Services/AjaxService";
import MonitoringCardSmartBed from "../../Components/MonitoringCardSmartBed/MonitoringCardSmartBed";
import MonitoringCardGateway from "../../Components/MonitoringCardGateway/MonitoringCardGateway";
import { canUseSetup, isGatewaySentinel, zoneLabel } from "../../Utils/DeviceTexts";

const LandingPage = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const sentinels: ISentinelRich[] = useSelector(
    (s: GlobalState) => s.generic.sentinels || []
  );
  const user = useSelector((s: GlobalState) => s.generic.user);

  const loadSentinels = () => {
    if (sentinels.length === 0) {
      setLoading(true);
    }
    ApiService.SentinelController.GetSentinels((response: IAPIResponse) => {
      if (response.error === null) {
        dispatch(GenericActions.setSentinels(response.parsed) as any);
      } else {
        ToastMessage("error", "Impossibile caricare i dispositivi.");
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    loadSentinels();
  }, []);

  // Dispositivi con un letto valido, ognuno nella sua zona (patient.floor), anche se spento
  const validSentinels = (sentinels ?? []).filter(
    (x: ISentinelRich) => x.patient && x.patient.bed !== "0"
  );

  // Zone in ordine di nome: l'ordine non cambia quando arriva un allarme;
  // le zone non date ("", "-") finiscono tutte in «Senza zona», in fondo
  const uniqueFloors = Array.from(
    new Set(validSentinels.map((x: ISentinelRich) => zoneLabel(x.patient.floor)))
  ).sort(
    (a, b) =>
      Number(a === zoneLabel("")) - Number(b === zoneLabel("")) ||
      a.localeCompare(b, "it", { numeric: true })
  );

  // nome della zona: se e' solo un numero, «Zona 1»
  const zoneTitle = (floor: string) => (/^\d+$/.test(floor) ? "Zona " + floor : floor);

  const renderCard = (x: ISentinelRich, i: number) =>
    isGatewaySentinel(x) ? (
      <MonitoringCardGateway index={i} sentinel={x} key={x.id} />
    ) : (
      <MonitoringCardSmartBed index={i} sentinel={x} key={x.id} />
    );

  // nella zona prima gli accesi, poi gli spenti; a parita', in ordine di numero
  const zoneSentinels = (floor: string, gateways: boolean) =>
    validSentinels
      .filter(
        (x: ISentinelRich) =>
          zoneLabel(x.patient.floor) === floor && isGatewaySentinel(x) === gateways
      )
      .sort(
        (x: ISentinelRich, y: ISentinelRich) => Number(!x.enabled) - Number(!y.enabled) || x.id - y.id
      );

  // Ogni zona e' una colonna, come le colonne di prima (scelta del titolare, 01/11/2026): i letti uno
  // sotto l'altro, poi le porte; le zone stanno affiancate e vanno a capo quando non c'e' posto
  const renderZone = (floor: string) => {
    const beds = zoneSentinels(floor, false);
    const doors = zoneSentinels(floor, true);
    return (
      <section className="home-zone" key={floor} aria-label={zoneTitle(floor)}>
        <h2>{zoneTitle(floor)}</h2>
        <div className="home-grid">
          {beds.map(renderCard)}
          {doors.map(renderCard)}
        </div>
      </section>
    );
  };

  return (
    <div className="fade-in-whole-page">
      <CustomCommandBar
        specials={[
          <div className="command-bar-title">Home</div>,
          <NavigationMenu />,
        ]}
        buttons={[]}
      />
      <div className="sliding-page-content home-page">
        <Loadable
          content={
            <div className="home-zones">
              {validSentinels.length === 0 && (
                <div className="home-empty">
                  <h2>Nessun dispositivo collegato</h2>
                  <p>
                    {canUseSetup(user)
                      ? "Aggiungi il letto o la porta dal Setup: poi compaiono qui, con il loro stato."
                      : "Quando l'installatore collega il letto o la porta, li vedi qui con il loro stato."}
                  </p>
                </div>
              )}
              {uniqueFloors.map(renderZone)}
            </div>
          }
          isLoading={loading}
        />
      </div>
    </div>
  );
};

export default LandingPage;
